// Units admin page (onboarding/frontend-self-serve, Jaime GO 2026-10-01):
// list units + create-unit form against the EXISTING endpoint
// POST /api/units/condo/:condoId (src/modules/unit/routes.js). Surfaces the
// API's validation/capacity/duplicate errors in Spanish. Also hosts the
// LINK-STANDALONE-UNIT action (onboarding/frontend-enable, 2026-10-01): input
// an 8-char unit link code from a propietario and pull their standalone unit
// into this condo — wired to the LIVE POST /api/units/link-by-code endpoint.
import { useEffect, useMemo, useState } from 'react';
import { unitsAPI } from '../services/api';
import api from '../services/api';
import { ManagementRoute } from '../components/RoleRoute';
import { useAuth } from '../contexts/AuthContext';
import { isPlanLimitError } from '../services/planLimitModal';

// Mirrors backend UNIT_NUMBER_CHARSET + UNIT_NUMBER_MAX_LENGTH (40) in
// src/modules/unit/routes.js (rule #292, Jaime 2026-10-01).
const UNIT_NUMBER_MAX_LENGTH = 40;
const UNIT_NUMBER_CHARSET = /^[A-Za-z0-9 \-._\/#áéíóúÁÉÍÓÚñÑ]+$/;

const unitText = (u: any) => u?.household_name || u?.name || u?.unit_number || `Unidad ${u?.id}`;

export default function BuildingUnits() {
  const { user, contexts, activeContextId } = useAuth();
  const activeContext = useMemo(() => contexts.find(c => c.id === activeContextId), [contexts, activeContextId]);
  const managedRoles = ['admin', 'super_admin', 'sub_admin'];
  const roleContext = contexts.find(c => c.kind === 'condo' && managedRoles.includes(c.role || ''));
  const condoId = activeContext?.condoId || roleContext?.condoId || (user?.condoId && managedRoles.includes(user.role) ? user.condoId : undefined);
  const condoLabel = activeContext?.label || contexts.find(c => c.condoId === condoId)?.label || 'Condominio';

  const [units, setUnits] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [unitNumber, setUnitNumber] = useState('');
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [linkCode, setLinkCode] = useState('');
  const [linkBusy, setLinkBusy] = useState(false);
  const [linkError, setLinkError] = useState('');

  const load = async () => {
    if (!condoId) return;
    setLoading(true);
    try {
      const u = await unitsAPI.getAll(condoId);
      const data = u.data?.data;
      setUnits(data?.units || (Array.isArray(data) ? data : u.data?.units || []));
      setError('');
    } catch {
      setError('No se pudieron cargar las unidades.');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [condoId]);

  const validateUnitNumber = (raw: string): string | null => {
    const value = raw.trim();
    if (!value) return 'El número de unidad es requerido';
    if (value.length > UNIT_NUMBER_MAX_LENGTH) return `El número de unidad no puede exceder ${UNIT_NUMBER_MAX_LENGTH} caracteres`;
    if (!UNIT_NUMBER_CHARSET.test(value)) return 'El número de unidad contiene caracteres no permitidos';
    return null;
  };

  const create = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (!condoId) return;
    const localError = validateUnitNumber(unitNumber);
    if (localError) { setFormError(localError); return; }
    setSaving(true); setFormError(''); setNotice('');
    try {
      await unitsAPI.createInCondo(condoId, { unitNumber: unitNumber.trim() });
      setNotice(`Unidad ${unitNumber.trim()} creada.`);
      setUnitNumber('');
      // Onboarding checklist v2: real action completes the 'Crea las unidades' step.
      window.dispatchEvent(new CustomEvent('vecindus:onboarding-done', { detail: 'units' }));
      await load();
    } catch (err: any) {
      if (!isPlanLimitError(err)) {
        const code = err?.response?.data?.code;
        const apiError = err?.response?.data?.error;
        if (code === 'UNIT_CAP_REACHED') setFormError('Límite de unidades del plan alcanzado.');
        else if (code === 'DUPLICATE_UNIT_NUMBER') setFormError('Ya existe una unidad con ese número en este condominio.');
        else setFormError(typeof apiError === 'string' && apiError ? apiError : 'No se pudo crear la unidad.');
      }
    } finally { setSaving(false); }
  };

  // Onboarding/frontend-enable (2026-10-01): accept an owner-shared 8-char
  // link code — LIVE backend POST /api/units/link-by-code { code, condoId }.
  const acceptLinkByCode = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (!condoId) return;
    const code = linkCode.trim().toUpperCase();
    if (!code) { setLinkError('Ingresa el código de vínculo.'); return; }
    setLinkBusy(true); setLinkError(''); setNotice('');
    try {
      const res = await api.post('/units/link-by-code', { code, condoId });
      const unitNumber = res.data?.data?.unitNumber || code;
      setNotice(`Unidad vinculada: ${unitNumber} se sumó a este condominio.`);
      setLinkCode('');
      await load();
    } catch (err: any) {
      const errorCode = err?.response?.data?.code;
      const apiError = err?.response?.data?.error;
      if (errorCode === 'CODE_INVALID') setLinkError('El código no es válido, ya fue utilizado o expiró.');
      else if (errorCode === 'ALREADY_LINKED') setLinkError('La unidad ya está vinculada a un condominio.');
      else if (errorCode === 'UNIT_CAP_REACHED') setLinkError('Límite de unidades del condominio alcanzado.');
      else if (errorCode === 'DUPLICATE_UNIT_NUMBER') setLinkError('Ya existe una unidad con ese número en este condominio.');
      else setLinkError(typeof apiError === 'string' && apiError ? apiError : 'No se pudo vincular la unidad. Verifica el código e intenta de nuevo.');
    } finally { setLinkBusy(false); }
  };

  if (!condoId) return <ManagementRoute><main className="p-6">Selecciona un condominio para administrar sus unidades.</main></ManagementRoute>;
  return (
    <ManagementRoute>
      <main className="mx-auto max-w-6xl space-y-6 p-6">
       
        <header>
          <div className="mb-2 inline-flex items-center gap-2 rounded-lg border border-teal-200 bg-teal-50 px-3 py-1.5 text-sm font-semibold text-teal-900"><span aria-hidden="true">⌂</span><span>Contexto activo: {condoLabel}</span></div>
          <h1 className="text-2xl font-bold">Unidades del condominio</h1>
          <p className="text-gray-600">Registra las unidades (apartamentos, casas, locales) y administra su vinculación.</p>
        </header>
        {error && <div className="rounded-lg bg-red-50 p-3 text-red-700">{error}</div>}
        {notice && <div className="rounded-lg border border-teal-200 bg-teal-50 p-3 text-sm text-teal-900">{notice}</div>}
        <section className="rounded-xl border bg-[var(--v-surface-1)] p-5">
          <h2 className="mb-4 text-lg font-semibold">Crear unidad</h2>
          <form onSubmit={create} className="grid gap-3 md:grid-cols-[1fr_auto]">
            <div>
              <input
                required
                maxLength={UNIT_NUMBER_MAX_LENGTH}
                placeholder="Número de unidad (ej. 101, TH1, A-3)"
                value={unitNumber}
                onChange={e => { setUnitNumber(e.target.value); setFormError(''); }}
                className="w-full rounded border p-2"
                aria-label="Número de unidad"
              />
              <p className="mt-1 text-xs text-gray-500">Hasta {UNIT_NUMBER_MAX_LENGTH} caracteres. Letras, números, espacios y -._/#</p>
            </div>
            <button disabled={saving} className="rounded bg-teal-700 px-4 py-2 font-semibold text-white disabled:opacity-50">{saving ? 'Creando…' : 'Crear unidad'}</button>
          </form>
          {formError && <div className="mt-3 rounded-lg bg-red-50 p-3 text-sm text-red-700">{formError}</div>}
        </section>
        <section className="rounded-xl border bg-[var(--v-surface-1)] p-5">
          <h2 className="mb-2 text-lg font-semibold">Vincular unidad independiente</h2>
          <p className="mb-3 text-sm text-gray-600">¿Un propietario registró su unidad por su cuenta? Ingresa el código de vínculo de 8 caracteres que te compartió para sumarla a este condominio.</p>
          <form onSubmit={acceptLinkByCode} className="grid gap-3 md:grid-cols-[1fr_auto]">
            <input
              placeholder="Código de vínculo (ej. AB12CD34)"
              value={linkCode}
              onChange={e => { setLinkCode(e.target.value); setLinkError(''); }}
              className="w-full rounded border p-2 uppercase tracking-widest"
              aria-label="Código de vínculo de unidad"
              maxLength={8}
              required
            />
            <button disabled={linkBusy} className="rounded bg-teal-700 px-4 py-2 font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50">{linkBusy ? 'Vinculando…' : 'Vincular unidad'}</button>
          </form>
          {linkError && <div className="mt-3 rounded-lg bg-red-50 p-3 text-sm text-red-700">{linkError}</div>}
        </section>
        <section className="rounded-xl border bg-[var(--v-surface-1)] p-5">
          <h2 className="mb-4 text-lg font-semibold">Unidades registradas ({units.length})</h2>
          {loading ? <p>Cargando unidades…</p> : units.length === 0 ? <p className="text-gray-600">Aún no hay unidades registradas. Crea la primera arriba.</p> : (
            <div className="space-y-3">
              {units.map((u) => (
                <div key={u.id} className="flex flex-wrap items-center justify-between gap-3 rounded border p-3">
                  <div>
                    <strong>{unitText(u)}</strong>
                    <p className="text-sm text-gray-600">
                      {u.unit_type || 'Unidad'}
                      {u.occupancy_status ? ` · ${u.occupancy_status === 'owner_occupied' ? 'ocupada por propietario' : u.occupancy_status}` : ''}
                      {u.owner_name ? ` · Propietario: ${u.owner_name}` : ''}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
    </ManagementRoute>
  );
}
